import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";

/**
 * StudentActivityTracker - Admin panel to see what students have been doing
 * Shows: Quiz attempts, Discussion posts, Files accessed, Last activity
 * NOW WITH: Real-time presence tracking using onSnapshot listener
 */
export default function StudentActivityTracker({ isAdmin }) {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [showOnlineOnly, setShowOnlineOnly] = useState(true); // Show only online by default
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (isAdmin) {
      loadAllStudents();
    }
  }, [isAdmin]);

  // Real-time listener for presence changes
  useEffect(() => {
    if (!isAdmin) return;

    const unsubscribe = onSnapshot(collection(db, "presence"), (snapshot) => {
      // Update students when presence changes
      setStudents(prevStudents => {
        const updatedStudents = prevStudents.map(student => {
          const presenceDoc = snapshot.docs.find(doc => doc.id === student.email);
          if (presenceDoc) {
            const data = presenceDoc.data();
            return {
              ...student,
              online: data.online && isRecentlyActive(data.lastSeen),
              lastActive: new Date(data.lastSeen || Date.now()),
              currentPage: data.currentPage || "Unknown",
            };
          }
          return student;
        });
        return updatedStudents.sort((a, b) => {
          if (a.online && !b.online) return -1;
          if (!a.online && b.online) return 1;
          return (b.lastActive?.getTime() || 0) - (a.lastActive?.getTime() || 0);
        });
      });
      setLastRefresh(new Date());
    });

    return () => unsubscribe();
  }, [isAdmin]);

  // Check if a user is recently active (within 5 minutes)
  const isRecentlyActive = (lastSeenStr) => {
    if (!lastSeenStr) return false;
    const lastSeen = new Date(lastSeenStr);
    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMins = diffMs / (1000 * 60);
    return diffMins < 5; // Active if seen within last 5 minutes
  };

  const loadAllStudents = async () => {
    setLoading(true);
    try {
      const studentMap = new Map();

      // 1. Load from users collection (ALL registered users)
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        usersSnap.docs.forEach((doc) => {
          const data = doc.data();
          const email = data.email;
          if (email) {
            studentMap.set(email, {
              email,
              name: data.name || data.displayName || "Unknown",
              batch: data.batch || "Unknown",
              quizCount: 0,
              lastActive: data.lastLogin ? new Date(data.lastLogin) : null,
              totalScore: 0,
              attempts: 0,
              photoURL: data.photoURL || null,
              online: false,
              currentPage: "Unknown",
            });
          }
        });
      } catch (e) {
        console.log("Could not load from users collection:", e.message);
      }

      // 2. Load from presence collection (currently online) - with timestamp validation
      try {
        const presenceSnap = await getDocs(collection(db, "presence"));
        presenceSnap.docs.forEach((doc) => {
          const data = doc.data();
          const email = doc.id;
          if (email && data.online && isRecentlyActive(data.lastSeen)) {
            if (!studentMap.has(email)) {
              studentMap.set(email, {
                email,
                name: data.name || "Unknown",
                batch: data.batch || "Unknown",
                quizCount: 0,
                lastActive: new Date(data.lastSeen || Date.now()),
                totalScore: 0,
                attempts: 0,
                online: true,
                photoURL: data.photoURL || null,
                currentPage: data.currentPage || "Unknown",
              });
            } else {
              const student = studentMap.get(email);
              student.online = true;
              student.lastActive = new Date(data.lastSeen || Date.now());
              student.currentPage = data.currentPage || "Unknown";
            }
          }
        });
      } catch (e) {
        console.log("Could not load from presence collection:", e.message);
      }

      // 3. Get quiz results data to find unique students with quiz history
      try {
        const quizSnap = await getDocs(
          query(
            collection(db, "quizResults"),
            orderBy("timestamp", "desc"),
            limit(1000)
          )
        );

        quizSnap.docs.forEach((doc) => {
          const data = doc.data();
          const email = data.studentEmail;
          if (email) {
            if (!studentMap.has(email)) {
              studentMap.set(email, {
                email,
                name: data.studentName || "Unknown",
                batch: data.batch || "Unknown",
                quizCount: 0,
                lastActive: null,
                totalScore: 0,
                attempts: 0,
              });
            }
            const student = studentMap.get(email);
            student.quizCount += 1;
            student.attempts += 1;
            student.totalScore += data.percent || 0;

            const timestamp = data.timestamp;
            if (timestamp) {
              const date = new Date(timestamp);
              if (!student.lastActive || date > new Date(student.lastActive)) {
                student.lastActive = date;
              }
            }
          }
        });
      } catch (e) {
        console.log("Could not load quiz results:", e.message);
      }

      const studentList = Array.from(studentMap.values()).sort((a, b) => {
        // Sort by online first, then by last active date
        if (a.online && !b.online) return -1;
        if (!a.online && b.online) return 1;
        return (b.lastActive?.getTime() || 0) - (a.lastActive?.getTime() || 0);
      });

      setStudents(studentList);
    } catch (error) {
      console.error("Error loading students:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentDetail = async (email) => {
    setLoading(true);
    try {
      const detail = {
        email,
        quizzes: [],
        discussions: [],
        visits: [],
      };

      console.log("Loading details for:", email);

      // Get quiz attempts
      try {
        const quizQ = query(
          collection(db, "quizResults"),
          where("studentEmail", "==", email),
          orderBy("timestamp", "desc"),
          limit(100)
        );
        const quizSnap = await getDocs(quizQ);
        console.log("Quiz results found:", quizSnap.docs.length);
        detail.quizzes = quizSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp
            ? new Date(doc.data().timestamp).toLocaleString("en-IN")
            : "N/A",
        }));
      } catch (e) {
        console.log("Quiz query error:", e.message);
      }

      // Get discussion posts
      try {
        const discussQ = query(
          collection(db, "facultyClassDiscussion"),
          where("authorEmail", "==", email),
          orderBy("timestamp", "desc"),
          limit(100)
        );
        const discussSnap = await getDocs(discussQ);
        console.log("Discussion posts found:", discussSnap.docs.length);
        detail.discussions = discussSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp
            ? new Date(doc.data().timestamp).toLocaleString("en-IN")
            : "N/A",
        }));
      } catch (e) {
        console.log("Discussion query error:", e.message);
      }

      // Get visit log entries
      try {
        const visitQ = query(
          collection(db, "visitLog"),
          where("email", "==", email),
          orderBy("loginAt", "desc"),
          limit(50)
        );
        const visitSnap = await getDocs(visitQ);
        console.log("Visit log entries found:", visitSnap.docs.length);
        detail.visits = visitSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          loginAt: doc.data().loginAt
            ? new Date(doc.data().loginAt).toLocaleString("en-IN")
            : "N/A",
        }));
      } catch (e) {
        console.log("Visit log query error:", e.message);
      }

      setStudentDetail(detail);
    } catch (error) {
      console.error("Error loading student detail:", error);
      alert("Error loading student details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    loadStudentDetail(student.email);
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    const query = searchEmail.toLowerCase().trim();
    const student = students.find(
      (s) =>
        s.email.toLowerCase().includes(query) ||
        (s.name && s.name.toLowerCase().includes(query))
    );
    if (student) {
      handleSelectStudent(student);
    } else {
      alert(`Student "${searchEmail}" not found. Make sure they have taken at least one quiz.`);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: "20px", color: "#c62828", fontWeight: 600 }}>
        ⛔ Only admins can view student activity
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
          borderRadius: 14,
          padding: "24px",
          marginBottom: 24,
          color: "#fff",
        }}
      >
        <h1 style={{ margin: "0 0 8px", fontSize: "1.6rem", fontWeight: 800 }}>
          📊 Student Activity Tracker
        </h1>
        <p style={{ margin: 0, fontSize: "0.95rem", fontWeight: 500 }}>
          Track what students are checking: quizzes, discussions, materials, and
          more
        </p>
      </div>

      {/* Search Bar & Refresh Button */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search by name or email (e.g., Manish Reddy or manish@example.com)"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          style={{
            flex: 1,
            minWidth: "250px",
            padding: "10px 12px",
            border: "1.5px solid #c5cae9",
            borderRadius: 8,
            fontSize: "0.9rem",
          }}
        />
        <button
          onClick={handleSearch}
          style={{
            background: "#1a237e",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          🔍 Search
        </button>
        <button
          onClick={() => loadAllStudents()}
          style={{
            background: "#4caf50",
            color: "#fff",
            border: "none",
            padding: "10px 16px",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.85rem",
          }}
          title="Manually refresh student list"
        >
          🔄 Refresh
        </button>
        <div style={{
          fontSize: "0.75rem",
          color: "#666",
          padding: "8px 12px",
          background: "#f5f5f5",
          borderRadius: 6,
          whiteSpace: "nowrap",
        }}>
          Last updated: {lastRefresh.toLocaleTimeString("en-IN")}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: "20px",
          alignItems: "start",
        }}
      >
        {/* Students List */}
        <div
          style={{
            background: "#fff",
            border: "1.5px solid #e8eaf6",
            borderRadius: 10,
            padding: "18px",
            maxHeight: "700px",
            overflowY: "auto",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <h3
              style={{
                margin: "0 0 10px",
                color: "#1a237e",
                fontSize: "1.05rem",
                fontWeight: 700,
              }}
            >
              👥 {showOnlineOnly ? "Currently Online" : "All Students"} ({showOnlineOnly ? students.filter((s) => s.online).length : students.length})
            </h3>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <div
                style={{
                  background: "#c8e6c9",
                  border: "2px solid #4caf50",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#1b5e20",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "1.4rem" }}>🟢</span>
                <span>Online Now: <strong>{students.filter((s) => s.online).length}</strong></span>
              </div>
              <button
                onClick={() => setShowOnlineOnly(!showOnlineOnly)}
                style={{
                  background: showOnlineOnly ? "#1a237e" : "#e0e0e0",
                  color: showOnlineOnly ? "#fff" : "#1a237e",
                  border: "1px solid #c5cae9",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {showOnlineOnly ? "👁️ Online Only" : "👥 Show All"}
              </button>
            </div>
          </div>

          {loading && students.length === 0 ? (
            <p style={{ color: "#666", textAlign: "center" }}>⏳ Loading...</p>
          ) : students.length === 0 ? (
            <p style={{ color: "#999", textAlign: "center" }}>No students yet</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {students
                .filter((s) => !showOnlineOnly || s.online)
                .map((student) => (
                <button
                  key={student.email}
                  onClick={() => handleSelectStudent(student)}
                  style={{
                    background:
                      selectedStudent?.email === student.email
                        ? "#1a237e"
                        : student.online
                        ? "#e8f5e9"
                        : "#f5f7ff",
                    color:
                      selectedStudent?.email === student.email
                        ? "#fff"
                        : student.online
                        ? "#1b5e20"
                        : "#1a237e",
                    border: student.online
                      ? "1.5px solid #4caf50"
                      : "1px solid #c5cae9",
                    borderRadius: 8,
                    padding: "12px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontWeight: 600,
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "6px", fontWeight: student.online ? 700 : 600 }}>
                    {student.online && (
                      <span style={{ fontSize: "1.1rem", animation: "pulse 2s infinite" }}>
                        🟢
                      </span>
                    )}
                    {student.name || student.email}
                    {student.online && (
                      <span
                        style={{
                          background: "#4caf50",
                          color: "#fff",
                          fontSize: "0.65rem",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: 700,
                          marginLeft: "auto",
                        }}
                      >
                        ACTIVE NOW
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                    📧 {student.email}
                  </div>
                  {student.online && student.currentPage && (
                    <div style={{ fontSize: "0.75rem", marginTop: "4px", color: "#4caf50", fontWeight: 600 }}>
                      📍 Now viewing: <strong>{student.currentPage}</strong>
                    </div>
                  )}
                  <div style={{ fontSize: "0.75rem", marginTop: "4px" }}>
                    🎯 {student.quizCount} quizzes · Last:{" "}
                    {student.lastActive
                      ? new Date(student.lastActive).toLocaleDateString(
                          "en-IN"
                        )
                      : "N/A"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Student Details */}
        <div>
          {selectedStudent && studentDetail ? (
            <div>
              {/* Student Header */}
              <div
                style={{
                  background: selectedStudent.online ? "#e8f5e9" : "#e3f2fd",
                  border: selectedStudent.online ? "1.5px solid #4caf50" : "1.5px solid #90caf9",
                  borderRadius: 10,
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                  <h2 style={{ margin: 0, color: "#1a237e" }}>
                    {selectedStudent.name}
                  </h2>
                  {selectedStudent.online && (
                    <span style={{
                      background: "#4caf50",
                      color: "#fff",
                      padding: "4px 10px",
                      borderRadius: 4,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      animation: "pulse 2s infinite",
                    }}>
                      🟢 ONLINE NOW
                    </span>
                  )}
                </div>
                <p style={{ margin: "0 0 6px", color: "#666", fontSize: "0.9rem" }}>
                  📧 {selectedStudent.email}
                </p>
                <p style={{ margin: "0 0 6px", color: "#666", fontSize: "0.9rem" }}>
                  👥 Batch: {selectedStudent.batch}
                </p>
                {selectedStudent.online && selectedStudent.currentPage && (
                  <p style={{ margin: "0 0 6px", color: "#2e7d32", fontSize: "0.9rem", fontWeight: 600 }}>
                    📍 Currently viewing: <strong>{selectedStudent.currentPage}</strong>
                  </p>
                )}
                {selectedStudent.lastActive && (
                  <p style={{ margin: "0 0 6px", color: "#666", fontSize: "0.85rem" }}>
                    ⏰ Last activity: {selectedStudent.lastActive.toLocaleString("en-IN")}
                  </p>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "12px",
                    marginTop: "12px",
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      padding: "10px",
                      borderRadius: 6,
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.4rem",
                        fontWeight: 800,
                        color: "#1e88e5",
                      }}
                    >
                      {selectedStudent.quizCount}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#666" }}>
                      Quiz Attempts
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      padding: "10px",
                      borderRadius: 6,
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.4rem",
                        fontWeight: 800,
                        color: "#43a047",
                      }}
                    >
                      {Math.round(
                        selectedStudent.totalScore /
                          (selectedStudent.attempts || 1)
                      )}
                      %
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#666" }}>
                      Avg Score
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      padding: "10px",
                      borderRadius: 6,
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.4rem",
                        fontWeight: 800,
                        color: "#fb8c00",
                      }}
                    >
                      {studentDetail.discussions.length}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#666" }}>
                      Discussions
                    </div>
                  </div>
                </div>
              </div>

              {/* Visit Log Tab */}
              {studentDetail.visits && studentDetail.visits.length > 0 && (
                <div
                  style={{
                    background: "#fff",
                    border: "1.5px solid #e8eaf6",
                    borderRadius: 10,
                    padding: "18px",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 14px",
                      color: "#1a237e",
                      fontSize: "1.05rem",
                      fontWeight: 700,
                    }}
                  >
                    📍 Login History ({studentDetail.visits.length})
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {studentDetail.visits.slice(0, 10).map((visit) => (
                      <div
                        key={visit.id}
                        style={{
                          background: "#f5f7ff",
                          border: "1px solid #e0e4f3",
                          borderRadius: 8,
                          padding: "10px",
                          fontSize: "0.8rem",
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#1a237e" }}>
                          ⏰ {visit.loginAt}
                        </div>
                        <div style={{ color: "#666", marginTop: "2px" }}>
                          📅 {visit.date || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quizzes Tab */}
              <div
                style={{
                  background: "#fff",
                  border: "1.5px solid #e8eaf6",
                  borderRadius: 10,
                  padding: "18px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 14px",
                    color: "#1a237e",
                    fontSize: "1.05rem",
                    fontWeight: 700,
                  }}
                >
                  📝 Quiz Attempts ({studentDetail.quizzes.length})
                </h3>
                {studentDetail.quizzes.length === 0 ? (
                  <p style={{ color: "#999", margin: 0 }}>No quiz attempts yet</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {studentDetail.quizzes.map((quiz) => (
                      <div
                        key={quiz.id}
                        style={{
                          background: "#f5f7ff",
                          border: "1px solid #e0e4f3",
                          borderRadius: 8,
                          padding: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <div style={{ fontWeight: 600, color: "#1a237e" }}>
                            {quiz.quizTitle || "Quiz"}
                          </div>
                          <div
                            style={{
                              background:
                                (quiz.percent || 0) >= 60 ? "#c8e6c9" : "#ffccbc",
                              color:
                                (quiz.percent || 0) >= 60 ? "#1b5e20" : "#bf360c",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            {quiz.percent || 0}%
                          </div>
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          ⏰ {quiz.timestamp}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          Score: {quiz.score || 0} / {quiz.total || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Discussions Tab */}
              {studentDetail.discussions.length > 0 && (
                <div
                  style={{
                    background: "#fff",
                    border: "1.5px solid #e8eaf6",
                    borderRadius: 10,
                    padding: "18px",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 14px",
                      color: "#1a237e",
                      fontSize: "1.05rem",
                      fontWeight: 700,
                    }}
                  >
                    💬 Discussion Posts ({studentDetail.discussions.length})
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {studentDetail.discussions.slice(0, 5).map((post) => (
                      <div
                        key={post.id}
                        style={{
                          background: "#f5f7ff",
                          border: "1px solid #e0e4f3",
                          borderRadius: 8,
                          padding: "12px",
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#1a237e", marginBottom: "4px" }}>
                          {post.text?.substring(0, 80)}...
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          ⏰ {post.timestamp}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#0097a7", marginTop: "4px" }}>
                          {post.visibility === "private" ? "🔒 Private" : "🌐 Public"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                background: "#f5f7ff",
                border: "1.5px dashed #c5cae9",
                borderRadius: 10,
                padding: "40px 20px",
                textAlign: "center",
                color: "#666",
              }}
            >
              <div style={{ fontSize: "2rem", marginBottom: "10px" }}>👈</div>
              <p style={{ margin: 0, fontWeight: 500 }}>
                Select a student to view their activity
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
